import { z } from 'zod'

import { isoInstantSchema } from '../common'

/**
 * A9–A11 · Price Lock (decision D4).
 *
 * The PIN is **exactly 4 digits** — it is a shoulder-surfing guard at a mandi,
 * not a credential; the password is the credential. Unlocking sets
 * `sessions.money_unlocked_until = now + autoLockSeconds`, and after that every
 * money key vanishes from every payload again without the client doing
 * anything (§4.16). Nothing on the client "hides" a figure it holds.
 */
export const pinSchema = z.string().regex(/^\d{4}$/, 'The PIN is 4 digits.')

/* ── A9 · POST /price-lock/unlock ─────────────────────────────────────────── */

export const priceLockUnlockRequest = z.object({ pin: pinSchema })

export const priceLockUnlockResponse = z.object({
  unlockedUntil: isoInstantSchema,
})

/* ── A10 · POST /price-lock/lock ──────────────────────────────────────────── */

export const priceLockLockResponse = z.object({
  locked: z.literal(true),
  lockedAt: isoInstantSchema,
})

/* ── A11 · POST /price-lock/pin (undesigned, U6) ──────────────────────────── */

/**
 * `currentPin` is required to change an existing PIN and absent when setting
 * the first one. Which case applies is `priceLock.pinSet` in `/me`; the server
 * decides for itself and does not trust the client's opinion of it.
 */
export const priceLockSetPinRequest = z.object({
  currentPin: pinSchema.optional(),
  newPin: pinSchema,
})

export const priceLockPinResponse = z.object({
  pinSet: z.literal(true),
  changedAt: isoInstantSchema,
})

export type PriceLockUnlockRequest = z.input<typeof priceLockUnlockRequest>
export type PriceLockUnlockResponse = z.infer<typeof priceLockUnlockResponse>
export type PriceLockLockResponse = z.infer<typeof priceLockLockResponse>
export type PriceLockSetPinRequest = z.input<typeof priceLockSetPinRequest>
export type PriceLockPinResponse = z.infer<typeof priceLockPinResponse>
